/**
 * CSV processing service for financial data
 * 1. Fetch JSON from API endpoint
 * 2. Convert to CSV format
 * 3. Upload to S3 via backend endpoint
 */

import { env } from '../utils/env'
import { CognitoAuthService } from './cognitoAuth'
import {
  transformApiResponseForCsv,
  type DataSourceHeaderConfig,
  getCsvHeaders
} from '../config/csvHeaderMapping'

export interface CsvUploadResponse {
  success: boolean
  message: string
  data: {
    s3_url: string
    s3_bucket: string
    s3_key: string
    original_filename: string
    file_size: number
    uploaded_at: string
  }
}

/**
 * Escape a single CSV field value
 */
function escapeCsvField(value: unknown): string {
  if (value === null || value === undefined) {
    return ''
  }
  if (typeof value === 'object') {
    // Serialize nested objects/arrays as JSON strings
    value = JSON.stringify(value)
  }
  const str = String(value)
  // Wrap in quotes if field contains comma, quote, newline, or carriage return
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

/**
 * Convert JSON array to CSV string with proper formatting
 */
function convertJsonArrayToCsv(jsonDataArray: unknown[]): string {
  if (!jsonDataArray || jsonDataArray.length === 0) {
    throw new Error('No data provided for CSV conversion')
  }

  // Collect union of all keys across every record so no column is lost
  const headerSet = new Set<string>()
  for (const dataRecord of jsonDataArray) {
    if (dataRecord && typeof dataRecord === 'object') {
      for (const key of Object.keys(dataRecord as Record<string, unknown>)) {
        headerSet.add(key)
      }
    }
  }
  const csvHeaders = Array.from(headerSet)

  // Build CSV content rows
  const csvContentRows: string[] = []

  // Add header row (escape header names as well)
  csvContentRows.push(csvHeaders.map(escapeCsvField).join(','))

  // Add data rows - use undefined-safe fallback for missing keys
  for (const dataRecord of jsonDataArray) {
    const record = (dataRecord && typeof dataRecord === 'object')
      ? (dataRecord as Record<string, unknown>)
      : {}
    const recordValues = csvHeaders.map(header => escapeCsvField(record[header]))
    csvContentRows.push(recordValues.join(','))
  }

  return csvContentRows.join('\n')
}

export type CsvSourceType = 'bank_a' | 'bank_b' | 'custodian_a' | 'custodian_b'

/**
 * Detect source type from CSV headers
 */
export function detectSourceTypeFromHeaders(headers: string[]): CsvSourceType | null {
  const headerSet = new Set(headers.map(h => h.trim()))

  // Custodian schemas have portfolio/holdings-specific columns
  const custodianMarkers = ['ticker_symbol', 'cusip_identifier', 'SecurityName', 'HeldQuantity', 'AssetCategory']
  if (custodianMarkers.every(h => headerSet.has(h))) {
    // custodian_b vs custodian_a: both have identical headers currently
    // Distinguish by presence of bank-style columns
    return headerSet.has('designation') ? 'custodian_b' : 'custodian_a'
  }

  // Bank schemas have designation, joining_date, is_active
  const bankMarkers = ['designation', 'company', 'joining_date', 'is_active', 'risk_profile']
  if (bankMarkers.every(h => headerSet.has(h))) {
    // bank_a: emergency_contact_name appears right after email_address (before phone_number)
    // bank_b: phone_number appears right after email_address (before emergency_contact_name)
    const idx_email = headers.indexOf('email_address')
    const idx_phone = headers.indexOf('phone_number')
    const idx_emergency = headers.indexOf('emergency_contact_name')
    if (idx_email !== -1 && idx_phone !== -1 && idx_emergency !== -1) {
      return idx_emergency < idx_phone ? 'bank_a' : 'bank_b'
    }
    return 'bank_a'
  }

  return null
}

/**
 * Detect source type from CSV file content
 */
export function detectSourceTypeFromCsv(csvContent: string): CsvSourceType | null {
  const lines = csvContent.split('\n')
  const headers = lines[0].split(',').map(h => h.trim())

  // If CSV has an explicit 'source' column, read its value from the first data row
  const sourceIdx = headers.indexOf('source')
  if (sourceIdx !== -1 && lines.length > 1) {
    const firstDataRow = lines[1].split(',')
    const sourceValue = firstDataRow[sourceIdx]?.trim().toLowerCase()
    const validSources: CsvSourceType[] = ['bank_a', 'bank_b', 'custodian_a', 'custodian_b']
    if (validSources.includes(sourceValue as CsvSourceType)) {
      return sourceValue as CsvSourceType
    }
  }

  return detectSourceTypeFromHeaders(headers)
}


async function uploadCsvToS3(
  csvContent: string,
  fileName: string,
  accessToken: string,
  sourceType?: CsvSourceType,
  onProgressUpdate?: (message: string) => void
): Promise<CsvUploadResponse> {
  onProgressUpdate?.('Uploading CSV file to S3...')

  const csvBlob = new Blob([csvContent], { type: 'text/csv' })
  const csvFile = new File([csvBlob], fileName, { type: 'text/csv' })

  const formData = new FormData()
  formData.append('file', csvFile)

  // Auto-detect source type from headers if not provided
  const resolvedSourceType = sourceType ?? detectSourceTypeFromCsv(csvContent)
  if (resolvedSourceType) {
    formData.append('source_type', resolvedSourceType)
  }

  const response = await fetch(`${env.API_URL}/${env.API_VERSION}/csv-uploads/upload`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${accessToken}` },
    body: formData,
  })

  if (!response.ok) {
    throw new Error(`CSV upload to S3 failed: ${response.status} ${response.statusText}`)
  }

  return response.json() as Promise<CsvUploadResponse>
}

/**
 * Fetch JSON data from API, convert to CSV, and upload to S3
 */
export async function processApiDataToCsv(
  sourceApiUrl: string,
  csvFileName: string,
  onProgressUpdate?: (progressMessage: string) => void
): Promise<CsvUploadResponse> {

  // Validate authentication token
  const authTokens = CognitoAuthService.getStoredTokens()
  if (!authTokens?.accessToken) {
    throw new Error('Authentication required. Please log in to continue.')
  }

  try {
    // Step 1: Fetch JSON data from source API
    onProgressUpdate?.('Fetching data from source API...')

    const apiResponse = await fetch(sourceApiUrl, {
      headers: {
        'Authorization': `Bearer ${authTokens.accessToken}`,
        'Content-Type': 'application/json'
      }
    })

    if (!apiResponse.ok) {
      throw new Error(`Source API request failed: ${apiResponse.status} ${apiResponse.statusText}`)
    }

    const responseData = await apiResponse.json()

    // Extract array from response (handle different API response structures)
    let extractedDataArray: unknown[]
    if (Array.isArray(responseData)) {
      extractedDataArray = responseData
    } else if (responseData.data && Array.isArray(responseData.data)) {
      extractedDataArray = responseData.data
    } else if (typeof responseData === 'object') {
      const foundArrayField = Object.values(responseData as Record<string, unknown>).find(value => Array.isArray(value))
      if (foundArrayField) {
        extractedDataArray = foundArrayField as unknown[]
      } else {
        extractedDataArray = [responseData]
      }
    } else {
      throw new Error('Invalid API response format - expected JSON array or object containing data array')
    }

    if (extractedDataArray.length === 0) {
      throw new Error('No data records found in API response')
    }

    // Step 2: Convert JSON data to CSV format
    onProgressUpdate?.('Converting JSON data to CSV format...')
    const csvFileContent = convertJsonArrayToCsv(extractedDataArray)

    // Step 3: Upload to S3
    const finalCsvFileName = csvFileName.endsWith('.csv') ? csvFileName : `${csvFileName}.csv`
    const uploadResult = await uploadCsvToS3(csvFileContent, finalCsvFileName, authTokens.accessToken, undefined, onProgressUpdate)

    onProgressUpdate?.('CSV file successfully uploaded to S3!')
    return uploadResult

  } catch (error) {
    console.error('Error in processApiDataToCsv:', error)
    throw error
  }
}

/**
 * Convert existing JSON data to CSV and upload to S3
 */
export async function convertAndUploadJsonToCsv(
  existingJsonData: unknown[],
  csvFileName: string,
  onProgressUpdate?: (progressMessage: string) => void,
  sourceType?: CsvSourceType
): Promise<CsvUploadResponse> {

  const authTokens = CognitoAuthService.getStoredTokens()
  if (!authTokens?.accessToken) {
    throw new Error('Authentication required. Please log in to continue.')
  }

  try {
    // Step 1: Inject source column into each row if sourceType is provided
    const dataWithSource = sourceType
      ? existingJsonData.map(row =>
          row && typeof row === 'object'
            ? { ...(row as Record<string, unknown>), source: sourceType }
            : row
        )
      : existingJsonData

    // Step 2: Convert JSON data to CSV format
    onProgressUpdate?.('Converting JSON data to CSV format...')
    const csvFileContent = convertJsonArrayToCsv(dataWithSource)

    // Step 3: Upload to S3 with source type
    const finalCsvFileName = csvFileName.endsWith('.csv') ? csvFileName : `${csvFileName}.csv`
    const uploadResult = await uploadCsvToS3(csvFileContent, finalCsvFileName, authTokens.accessToken, sourceType, onProgressUpdate)

    onProgressUpdate?.('CSV file successfully uploaded to S3!')
    return uploadResult

  } catch (error) {
    console.error('Error in convertAndUploadJsonToCsv:', error)
    throw error
  }
}

/**
 * Convert JSON array to CSV using header mapping configuration for consistent headers
 */
export function convertJsonArrayToCsvWithMapping(
  jsonDataArray: unknown[],
  mappingType: keyof DataSourceHeaderConfig
): string {
  if (!jsonDataArray || jsonDataArray.length === 0) {
    throw new Error('No data provided for CSV conversion')
  }

  // Use predefined CSV headers from configuration
  const csvHeaders = getCsvHeaders(mappingType)

  // Transform data to use CSV headers as keys
  const transformedData = jsonDataArray.map(item => {
    if (item && typeof item === 'object') {
      return transformApiResponseForCsv(item as Record<string, unknown>, mappingType)
    }
    return {}
  })

  // Build CSV content rows
  const csvContentRows: string[] = []

  // Add header row
  csvContentRows.push(csvHeaders.map(escapeCsvField).join(','))

  // Add data rows - use predefined headers to ensure consistency
  for (const dataRecord of transformedData) {
    const record = (dataRecord && typeof dataRecord === 'object')
      ? (dataRecord as Record<string, unknown>)
      : {}
    const recordValues = csvHeaders.map(header => escapeCsvField(record[header]))
    csvContentRows.push(recordValues.join(','))
  }

  return csvContentRows.join('\n')
}

/**
 * Convert existing JSON data to CSV with header mapping and upload to S3
 */
export async function convertAndUploadJsonToCsvWithMapping(
  existingJsonData: unknown[],
  mappingType: keyof DataSourceHeaderConfig,
  csvFileName: string,
  onProgressUpdate?: (progressMessage: string) => void
): Promise<CsvUploadResponse> {

  const authTokens = CognitoAuthService.getStoredTokens()
  if (!authTokens?.accessToken) {
    throw new Error('Authentication required. Please log in to continue.')
  }

  try {
    // Step 1: Convert JSON data to CSV format with header mapping
    onProgressUpdate?.('Converting JSON data to CSV format with consistent headers...')
    const csvFileContent = convertJsonArrayToCsvWithMapping(existingJsonData, mappingType)

    // Step 2: Upload to S3
    const finalCsvFileName = csvFileName.endsWith('.csv') ? csvFileName : `${csvFileName}.csv`
    const uploadResult = await uploadCsvToS3(csvFileContent, finalCsvFileName, authTokens.accessToken, undefined, onProgressUpdate)

    onProgressUpdate?.('CSV file successfully uploaded to S3!')
    return uploadResult

  } catch (error) {
    console.error('Error in convertAndUploadJsonToCsvWithMapping:', error)
    throw error
  }
}

/**
 * Process API data to CSV with header mapping for consistent format
 */
export async function processApiDataToCsvWithMapping(
  sourceApiUrl: string,
  mappingType: keyof DataSourceHeaderConfig,
  csvFileName: string,
  onProgressUpdate?: (progressMessage: string) => void
): Promise<CsvUploadResponse> {

  // Validate authentication token
  const authTokens = CognitoAuthService.getStoredTokens()
  if (!authTokens?.accessToken) {
    throw new Error('Authentication required. Please log in to continue.')
  }

  try {
    // Step 1: Fetch JSON data from source API
    onProgressUpdate?.('Fetching data from source API...')

    const apiResponse = await fetch(sourceApiUrl, {
      headers: {
        'Authorization': `Bearer ${authTokens.accessToken}`,
        'Content-Type': 'application/json'
      }
    })

    if (!apiResponse.ok) {
      throw new Error(`Source API request failed: ${apiResponse.status} ${apiResponse.statusText}`)
    }

    const responseData = await apiResponse.json()

    // Extract array from response (handle different API response structures)
    let extractedDataArray: unknown[]
    if (Array.isArray(responseData)) {
      extractedDataArray = responseData
    } else if (responseData.data && Array.isArray(responseData.data)) {
      extractedDataArray = responseData.data
    } else if (typeof responseData === 'object') {
      const foundArrayField = Object.values(responseData as Record<string, unknown>).find(value => Array.isArray(value))
      if (foundArrayField) {
        extractedDataArray = foundArrayField as unknown[]
      } else {
        extractedDataArray = [responseData]
      }
    } else {
      throw new Error('Invalid API response format - expected JSON array or object containing data array')
    }

    if (extractedDataArray.length === 0) {
      throw new Error('No data records found in API response')
    }

    // Step 2: Convert JSON data to CSV format with header mapping
    onProgressUpdate?.('Converting JSON data to CSV format with consistent headers...')
    const csvFileContent = convertJsonArrayToCsvWithMapping(extractedDataArray, mappingType)

    // Step 3: Upload to S3
    const finalCsvFileName = csvFileName.endsWith('.csv') ? csvFileName : `${csvFileName}.csv`
    const uploadResult = await uploadCsvToS3(csvFileContent, finalCsvFileName, authTokens.accessToken, undefined, onProgressUpdate)

    onProgressUpdate?.('CSV file successfully uploaded to S3!')
    return uploadResult

  } catch (error) {
    console.error('Error in processApiDataToCsvWithMapping:', error)
    throw error
  }
}